import { storeToRefs } from "pinia";
import type { PurchaseOrder } from "../../redistribution/models/PurchaseOrder.js";
import {
  fetchRequestIdByPoNumber,
  searchPurchaseOrdersByPONumbers,
  searchPurchaseOrdersByRequestId,
} from "../../services/purchaseOrderService.ts";
import { useAppStore } from "../../stores/app.ts";
import { useDemoIdentifierSearch } from "./featureFlags.ts";
import {
  classifySearchInput,
  getLoadingMessage,
  getNoResultsMessage,
  getSearchTypeLabel,
} from "./searchTypeClassifier.ts";

const DEMO_RELATED_PO_POOL = [
  "1709268KCN",
  "1711590EHU",
  "1710946WOT",
  "1710960WOT",
  "1710962WOT",
  "1711207WOT",
  "1711208WOT",
  "1712621WOT",
  "1712622WOT",
  "1712624WOT",
  "1712625WOT",
  "1712626WOT",
  "1712628WOT",
  "1712631WOT",
  "1712633WOT",
  "1712635WOT",
  "1712636WOT",
  "1712637WOT",
  "1712638WOT",
  "1712639WOT",
  "1711463KGR",
  "1711465KGR",
  "1708202WOT",
  "1708203WOT",
  "1709268/1KCN",
  "1707062KGR",
  "1712349KGR",
  "1704705KGR",
  "1707715KCN",
  "1709220KCN",
  "1709219KCN",
  "1707721KCN",
  "1709267KCN",
  "1709221KCN",
  "1711681KCN",
  "1707720KCN",
  "1709539KCN",
  "1711664KCN",
  "1711618KCN",
  "1709422EHU",
  "1709467KCN",
  "1708053EHU",
  "1708052EHU",
  "1708046EHU",
  "1706462/1KCN",
  "1708044EHU",
  "1709457KCN",
  "1711672KCN",
  "1711671KCN",
] as const;

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function randomInt(min: number, max: number): number {
  const lower = Math.ceil(min);
  const upper = Math.floor(max);
  return Math.floor(Math.random() * (upper - lower + 1)) + lower;
}

function randomizeIsoDate(
  baseIso: string,
  minDayOffset: number,
  maxDayOffset: number
): string {
  const base = new Date(baseIso);
  if (Number.isNaN(base.getTime())) {
    return baseIso;
  }

  const offset = randomInt(minDayOffset, maxDayOffset);
  base.setDate(base.getDate() + offset);
  return base.toISOString();
}

function createDemoPrimaryPurchaseOrder(poNumber: string): PurchaseOrder {
  const today = new Date();
  const baseArrival = new Date(today);
  baseArrival.setDate(baseArrival.getDate() + randomInt(7, 30));
  const baseArrivalIso = baseArrival.toISOString();

  const demoSkuSeeds = [
    {
      skuNumber: "DEMO-1001",
      description: "Demo Sofa",
      color: "Charcoal",
      theme: "Modern",
      cubeSize: 45,
      qty: 12,
      stock: 80,
    },
    {
      skuNumber: "DEMO-1002",
      description: "Demo Loveseat",
      color: "Charcoal",
      theme: "Modern",
      cubeSize: 32,
      qty: 8,
      stock: 50,
    },
    {
      skuNumber: "DEMO-1003",
      description: "Demo Recliner",
      color: "Sand",
      theme: "Modern",
      cubeSize: 28,
      qty: 15,
      stock: 20,
    },
    {
      skuNumber: "DEMO-1004",
      description: "Demo Ottoman",
      color: "Sand",
      theme: "Modern",
      cubeSize: 12,
      qty: 20,
      stock: 95,
    },
    {
      skuNumber: "DEMO-1005",
      description: "Demo Accent Chair",
      color: "Indigo",
      theme: "Modern",
      cubeSize: 22,
      qty: 10,
      stock: 35,
    },
  ];

  const skus = demoSkuSeeds.map((seed) => {
    const lineItemQuantity = clampNumber(
      seed.qty + randomInt(-2, 6),
      1,
      99_999
    );
    const forecastQuantityAvailable = seed.stock + lineItemQuantity;
    return {
      skuNumber: seed.skuNumber,
      pmKey: seed.skuNumber,
      description: seed.description,
      lineItemQuantity,
      quantityAvailable: seed.stock,
      dailyRateOfSale: 2,
      daysToStockout: Math.floor(seed.stock / 2),
      urgency: 0,
      forecastQuantityAvailable,
      adjustedForecast: forecastQuantityAvailable,
      theme: seed.theme,
      cubeSize: seed.cubeSize,
      pieceCount: 1,
      color: seed.color,
      family: seed.skuNumber.slice(0, 4),
      stockedThroughDate: "",
      difference: 0,
    };
  });

  return {
    vendorCode: "DEMO",
    arrivalDate: baseArrivalIso,
    shipDate: baseArrivalIso,
    division: "FL",
    purchaseOrderNumber: poNumber,
    purchaseOrderRequestId: poNumber,
    warehouse: 99,
    purchaseOrderStatus: "Open",
    countUniqueSkus: skus.length,
    sumQuantities: skus.reduce((total, sku) => total + sku.lineItemQuantity, 0),
    cubeTotal: skus.reduce(
      (total, sku) => total + sku.cubeSize * sku.lineItemQuantity,
      0
    ),
    calcArrivalDate: baseArrivalIso,
    skus,
  };
}

function createDemoRelatedPurchaseOrders(
  primaryPurchaseOrder: PurchaseOrder
): PurchaseOrder[] {
  const shuffledPool = DEMO_RELATED_PO_POOL.filter(
    (poNumber) => poNumber !== primaryPurchaseOrder.purchaseOrderNumber
  ).toSorted(() => Math.random() - 0.5);

  const targetCount = randomInt(3, Math.min(50, shuffledPool.length));
  const selectedPoNumbers = shuffledPool.slice(0, targetCount);

  return selectedPoNumbers.map((poNumber) => {
    const demoSkus = primaryPurchaseOrder.skus.map((sku) => {
      const quantityDelta = randomInt(-2, 6);
      const forecastDelta = randomInt(-8, 8);
      const nextLineItemQuantity = clampNumber(
        sku.lineItemQuantity + quantityDelta,
        1,
        99_999
      );
      return {
        ...sku,
        lineItemQuantity: nextLineItemQuantity,
        forecastQuantityAvailable:
          sku.forecastQuantityAvailable + forecastDelta,
      };
    });

    const sumQuantities = demoSkus.reduce(
      (total, sku) => total + sku.lineItemQuantity,
      0
    );
    const cubeTotal = demoSkus.reduce(
      (total, sku) => total + sku.cubeSize * sku.lineItemQuantity,
      0
    );

    return {
      ...primaryPurchaseOrder,
      purchaseOrderNumber: poNumber,
      shipDate: randomizeIsoDate(primaryPurchaseOrder.shipDate, -5, 5),
      arrivalDate: randomizeIsoDate(primaryPurchaseOrder.arrivalDate, -3, 10),
      calcArrivalDate: randomizeIsoDate(
        primaryPurchaseOrder.calcArrivalDate ||
          primaryPurchaseOrder.arrivalDate,
        -3,
        10
      ),
      sumQuantities,
      cubeTotal,
      skus: demoSkus,
      purchaseOrderRequestId:
        primaryPurchaseOrder.purchaseOrderRequestId ??
        primaryPurchaseOrder.purchaseOrderNumber,
    };
  });
}

const SESSION_COOKIE_NAMES = {
  purchaseOrderNumber: "PurchaseOrderNumber",
  purchaseOrderRequestId: "PurchaseOrderRequestId",
  purchaseOrderNumbers: "PurchaseOrderNumbers",
} as const;

const HISTORY_COOKIE_NAMES = {
  purchaseOrderNumber: "PurchaseOrderNumberHistory",
  purchaseOrderRequestId: "PurchaseOrderRequestIdHistory",
  purchaseOrderNumbers: "PurchaseOrderNumbersHistory",
} as const;

const COOKIE_MAX_AGE_SESSION_SECONDS = 8 * 60 * 60;
const COOKIE_MAX_AGE_HISTORY_SECONDS = 30 * 24 * 60 * 60;
const MAX_HISTORY_ENTRIES = 10;

/* eslint-disable unicorn/no-document-cookie */
function setCookie(name: string, value: string, maxAgeSeconds: number): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; SameSite=Lax; Max-Age=${String(maxAgeSeconds)}`;
}

function getCookie(name: string): string | undefined {
  const prefixedName = `${name}=`;
  const value = document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(prefixedName));
  if (!value) {
    return undefined;
  }
  return decodeURIComponent(value.slice(prefixedName.length));
}
/* eslint-enable unicorn/no-document-cookie */

function parseHistoryCookie(name: string): string[] {
  const raw = getCookie(name);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === "string")
      : [];
  } catch {
    return [];
  }
}

function updateHistoryCookie(name: string, entry: string | undefined): void {
  const normalizedEntry = entry?.trim();
  if (!normalizedEntry) {
    return;
  }

  const existing = parseHistoryCookie(name);
  const next = [
    normalizedEntry,
    ...existing.filter(
      (value) => value.toUpperCase() !== normalizedEntry.toUpperCase()
    ),
  ].slice(0, MAX_HISTORY_ENTRIES);

  setCookie(name, JSON.stringify(next), COOKIE_MAX_AGE_HISTORY_SECONDS);
}

function updatePurchaseOrderSearchCookies(args: {
  purchaseOrderNumber?: string | undefined;
  purchaseOrderRequestId?: string | undefined;
  purchaseOrderNumbers: string[];
}): void {
  const currentPurchaseOrderNumbers = args.purchaseOrderNumbers
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, 50);
  const purchaseOrderNumbersValue = currentPurchaseOrderNumbers.join(" ");
  setCookie(
    SESSION_COOKIE_NAMES.purchaseOrderNumbers,
    purchaseOrderNumbersValue,
    COOKIE_MAX_AGE_SESSION_SECONDS
  );

  const purchaseOrderNumber = args.purchaseOrderNumber?.trim();
  if (purchaseOrderNumber) {
    setCookie(
      SESSION_COOKIE_NAMES.purchaseOrderNumber,
      purchaseOrderNumber,
      COOKIE_MAX_AGE_SESSION_SECONDS
    );
    updateHistoryCookie(
      HISTORY_COOKIE_NAMES.purchaseOrderNumber,
      purchaseOrderNumber
    );
  }

  const purchaseOrderRequestId = args.purchaseOrderRequestId?.trim();
  if (purchaseOrderRequestId) {
    setCookie(
      SESSION_COOKIE_NAMES.purchaseOrderRequestId,
      purchaseOrderRequestId,
      COOKIE_MAX_AGE_SESSION_SECONDS
    );
    updateHistoryCookie(
      HISTORY_COOKIE_NAMES.purchaseOrderRequestId,
      purchaseOrderRequestId
    );
  }

  updateHistoryCookie(
    HISTORY_COOKIE_NAMES.purchaseOrderNumbers,
    purchaseOrderNumbersValue
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "Unknown error";
}

function getPurchaseOrderSearchErrorMessage(args: {
  submittedPoNumbersCount: number;
  error: unknown;
}): string {
  const rawMessage = getErrorMessage(args.error);
  const isTemporaryMultiPoValidationFailure =
    args.submittedPoNumbersCount > 1 &&
    /(\(400\)|\b400\b)/i.test(rawMessage) &&
    /only one po number is allowed|search by po numbers api failed/i.test(
      rawMessage
    );

  if (isTemporaryMultiPoValidationFailure) {
    return "Bulk PO search is temporarily unavailable while backend validation is being finalized. Please search one PO number at a time for now. Multi-PO support will return in a future release.";
  }

  return `Unable to load purchase orders right now. ${rawMessage}`;
}

function buildSinglePoFallbackSearchInput(poNumber: string): string {
  const normalized = poNumber.toUpperCase().replaceAll("/", "");
  if (normalized.length <= 7 || !/^\d{7}[A-Z0-9]+$/.test(normalized)) {
    return poNumber;
  }

  const withSlash = `${normalized.slice(0, 7)}/${normalized.slice(7)}`;
  return [...new Set([poNumber, normalized, withSlash])].join(" ");
}

async function searchRelatedPurchaseOrders(
  normalizedSubmittedPoNumbers: string,
  useDemoRelatedResults = false
): Promise<void> {
  const appStore = useAppStore();
  const { searching } = storeToRefs(appStore);
  const {
    resetPoSearchResultsContext,
    setPoSearchResultsContext,
    setSearchResults,
    clearTypedSearchPresentation,
  } = appStore;

  searching.value = true;
  setSearchResults([]);
  resetPoSearchResultsContext();
  let submittedPoNumbers: string[] = [];
  try {
    submittedPoNumbers = normalizedSubmittedPoNumbers
      .split(" ")
      .filter(Boolean);
    const firstSubmittedPoNumber = submittedPoNumbers[0];
    let requestIdFallbackError: string | undefined;
    let results: PurchaseOrder[];
    if (useDemoRelatedResults) {
      const primaryPoNumberFromInput = firstSubmittedPoNumber ?? "DEMO-PO";
      results = [createDemoPrimaryPurchaseOrder(primaryPoNumberFromInput)];
    } else {
      results = await searchPurchaseOrdersByPONumbers(
        submittedPoNumbers.join(" ")
      );

      if (
        submittedPoNumbers.length === 1 &&
        results.length === 0 &&
        firstSubmittedPoNumber !== undefined
      ) {
        const fallbackSearchInput = buildSinglePoFallbackSearchInput(
          firstSubmittedPoNumber
        );
        if (fallbackSearchInput !== submittedPoNumbers.join(" ")) {
          results = await searchPurchaseOrdersByPONumbers(fallbackSearchInput);
        }
      }

      if (
        submittedPoNumbers.length === 1 &&
        results.length === 0 &&
        firstSubmittedPoNumber !== undefined
      ) {
        try {
          const resolvedRequestId = await fetchRequestIdByPoNumber(
            firstSubmittedPoNumber
          );
          if (resolvedRequestId !== undefined) {
            results = await searchPurchaseOrdersByRequestId(resolvedRequestId);
          }
        } catch (error) {
          requestIdFallbackError = getErrorMessage(error);
          console.warn(
            "Single-PO request-id fallback failed",
            firstSubmittedPoNumber,
            requestIdFallbackError
          );
        }
      }
    }
    setSearchResults(results);

    if (submittedPoNumbers.length !== 1) {
      updatePurchaseOrderSearchCookies({
        purchaseOrderNumbers: submittedPoNumbers,
      });
      setPoSearchResultsContext({
        mode: "flat",
        primaryPoNumber: undefined,
        relatedPurchaseOrders: [],
        helperMessage:
          "Related PO expansion is available only when searching for a single PO number.",
        helperMessageTone: "info",
      });
      return;
    }

    const [primaryPurchaseOrder] = results;
    if (results.length !== 1 || primaryPurchaseOrder === undefined) {
      setPoSearchResultsContext({
        mode: "flat",
        primaryPoNumber: primaryPurchaseOrder?.purchaseOrderNumber,
        relatedPurchaseOrders: [],
        helperMessage: requestIdFallbackError
          ? `PO lookup returned no results, and request-id fallback failed: ${requestIdFallbackError}`
          : undefined,
        helperMessageTone: requestIdFallbackError ? "error" : undefined,
      });
      return;
    }

    const primaryPoNumber = primaryPurchaseOrder.purchaseOrderNumber;

    const requestId = primaryPurchaseOrder.purchaseOrderRequestId;
    if (!useDemoRelatedResults && requestId === undefined) {
      setPoSearchResultsContext({
        mode: "flat",
        primaryPoNumber,
        relatedPurchaseOrders: [],
        helperMessage: undefined,
        helperMessageTone: undefined,
      });
      return;
    }

    const relatedRequestId = requestId ?? "";
    const normalizedRequestId =
      typeof requestId === "number" ? String(requestId) : requestId?.trim();
    const hasRequestId =
      typeof requestId === "number" ||
      (typeof requestId === "string" && requestId.trim().length > 0);
    if (!useDemoRelatedResults && !hasRequestId) {
      setPoSearchResultsContext({
        mode: "flat",
        primaryPoNumber,
        relatedPurchaseOrders: [],
        helperMessage: undefined,
        helperMessageTone: undefined,
      });
      return;
    }

    let relatedPurchaseOrders: PurchaseOrder[];

    if (useDemoRelatedResults) {
      relatedPurchaseOrders =
        createDemoRelatedPurchaseOrders(primaryPurchaseOrder);
    } else {
      let relatedSearchResults: PurchaseOrder[];
      try {
        relatedSearchResults =
          await searchPurchaseOrdersByRequestId(relatedRequestId);
      } catch {
        updatePurchaseOrderSearchCookies({
          purchaseOrderNumber: primaryPoNumber,
          purchaseOrderRequestId: normalizedRequestId,
          purchaseOrderNumbers: [primaryPoNumber].filter(
            (value): value is string => !!value
          ),
        });
        setPoSearchResultsContext({
          mode: "flat",
          primaryPoNumber,
          relatedPurchaseOrders: [],
          helperMessage:
            "Unable to load related purchase orders right now. Showing primary results only.",
          helperMessageTone: "error",
        });
        return;
      }

      relatedPurchaseOrders = relatedSearchResults.filter(
        (purchaseOrder) =>
          purchaseOrder.purchaseOrderNumber !==
          primaryPurchaseOrder.purchaseOrderNumber
      );
    }

    if (relatedPurchaseOrders.length === 0) {
      updatePurchaseOrderSearchCookies({
        purchaseOrderNumber: primaryPoNumber,
        purchaseOrderRequestId: normalizedRequestId,
        purchaseOrderNumbers: [primaryPoNumber].filter(
          (value): value is string => !!value
        ),
      });
      setPoSearchResultsContext({
        mode: "flat",
        primaryPoNumber,
        relatedPurchaseOrders: [],
        helperMessage: undefined,
        helperMessageTone: undefined,
      });
      return;
    }

    const relatedPurchaseOrder = relatedPurchaseOrders[0];
    const helperMessage = useDemoRelatedResults
      ? `Demo mode: showing ${relatedPurchaseOrders.length.toString()} randomized related purchase orders for ${primaryPurchaseOrder.purchaseOrderNumber}.`
      : relatedPurchaseOrders.length === 1 && relatedPurchaseOrder
        ? `Showing ${relatedPurchaseOrder.purchaseOrderNumber} as a related purchase order for ${primaryPurchaseOrder.purchaseOrderNumber}.`
        : `Showing ${relatedPurchaseOrders.length.toString()} related purchase orders for ${primaryPurchaseOrder.purchaseOrderNumber}.`;

    updatePurchaseOrderSearchCookies({
      purchaseOrderNumber: primaryPoNumber,
      purchaseOrderRequestId: normalizedRequestId,
      purchaseOrderNumbers: [primaryPoNumber].filter(
        (value): value is string => !!value
      ),
    });

    setPoSearchResultsContext({
      mode: "single-po-related",
      primaryPoNumber,
      relatedPurchaseOrders,
      helperMessage,
      helperMessageTone: "info",
    });
  } catch (error) {
    setPoSearchResultsContext({
      mode: "flat",
      primaryPoNumber: undefined,
      relatedPurchaseOrders: [],
      helperMessage: getPurchaseOrderSearchErrorMessage({
        submittedPoNumbersCount: submittedPoNumbers.length,
        error,
      }),
      helperMessageTone: "error",
    });
  } finally {
    searching.value = false;
    clearTypedSearchPresentation();
  }
}

async function runTypedInputSearch(rawInput: string): Promise<void> {
  const appStore = useAppStore();
  const { searching } = storeToRefs(appStore);
  const {
    beginTypedSearch,
    setSearchResults,
    resetPoSearchResultsContext,
    setPoSearchResultsContext,
    clearTypedSearchPresentation,
  } = appStore;

  const classification = classifySearchInput(rawInput);
  const searchTypeLabel = getSearchTypeLabel(classification.type);
  beginTypedSearch({
    searchTypeLabel,
    loadingMessage: getLoadingMessage(
      classification.type,
      classification.tokens.length
    ),
    noResultsMessage: getNoResultsMessage(classification.type),
  });

  if (classification.type === "invalid") {
    setSearchResults([]);
    resetPoSearchResultsContext();
    setPoSearchResultsContext({
      mode: "flat",
      primaryPoNumber: undefined,
      relatedPurchaseOrders: [],
      helperMessage: classification.reason,
      helperMessageTone: "error",
    });
    return;
  }

  if (classification.type === "po-numbers") {
    await searchRelatedPurchaseOrders(classification.normalizedInput, false);
    clearTypedSearchPresentation();
    return;
  }

  searching.value = true;
  setSearchResults([]);
  resetPoSearchResultsContext();

  try {
    const useDemoSearch = useDemoIdentifierSearch;

    if (classification.type === "request-id") {
      const requestIdToken =
        classification.tokens[0] ?? classification.normalizedInput;
      if (useDemoSearch) {
        const demoResults = [0, 1, 2].map((_, index) => {
          const normalizedIdentifier = requestIdToken
            .toUpperCase()
            .replaceAll(/[^A-Z0-9]/g, "")
            .slice(0, 8);
          const suffix = String(index + 1).padStart(2, "0");
          return createDemoPrimaryPurchaseOrder(
            `${normalizedIdentifier}-${suffix}`
          );
        });
        setSearchResults(demoResults);
        setPoSearchResultsContext({
          mode: "flat",
          primaryPoNumber: undefined,
          relatedPurchaseOrders: [],
          helperMessage: "Showing demo Request ID results.",
          helperMessageTone: "info",
        });
      } else {
        const results = await searchPurchaseOrdersByRequestId(requestIdToken);
        setSearchResults(results);
        setPoSearchResultsContext({
          mode: "flat",
          primaryPoNumber: undefined,
          relatedPurchaseOrders: [],
          helperMessage: undefined,
          helperMessageTone: undefined,
        });
      }
      return;
    }

    if (classification.type === "cutting-id") {
      if (useDemoSearch) {
        const demoResults = [0, 1].map((_, index) => {
          const normalizedIdentifier = (
            classification.tokens[0] ?? classification.normalizedInput
          )
            .toUpperCase()
            .replaceAll(/[^A-Z0-9]/g, "")
            .slice(0, 8);
          const suffix = String(index + 1).padStart(2, "0");
          return createDemoPrimaryPurchaseOrder(
            `${normalizedIdentifier}-${suffix}`
          );
        });
        setSearchResults(demoResults);
        setPoSearchResultsContext({
          mode: "flat",
          primaryPoNumber: undefined,
          relatedPurchaseOrders: [],
          helperMessage: "Showing demo Cutting ID results.",
          helperMessageTone: "info",
        });
        return;
      }

      setSearchResults([]);
    }
  } catch (error) {
    setPoSearchResultsContext({
      mode: "flat",
      primaryPoNumber: undefined,
      relatedPurchaseOrders: [],
      helperMessage: `Unable to load purchase orders right now. ${getErrorMessage(error)}`,
      helperMessageTone: "error",
    });
  } finally {
    searching.value = false;
    clearTypedSearchPresentation();
  }
}

export function useTypedPurchaseOrderSearch() {
  const appStore = useAppStore();
  const { searching } = storeToRefs(appStore);

  return {
    searching,
    runTypedInputSearch,
  };
}
